import {
  AdapterBaseTime,
  getClientLanguages,
  getMatchLanguages,
  orderByLanguage,
  toWithinRange
} from "./chunk-BPZZ3HZX.js";
import {
  PlaybackSessionError
} from "./chunk-7BHS56SI.js";
import {
  getLogger
} from "./chunk-LFIBM62H.js";
import {
  isAbortError,
  toError
} from "./chunk-2GHSQBQ5.js";

// src/adapter/autoplay-error.ts
var AutoplayError = class _AutoplayError extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
    Object.setPrototypeOf(this, _AutoplayError.prototype);
    this.name = "AutoplayError";
  }
};

// src/adapter/adapter-base-htmlmedia.ts
import {
  DestroyableSet,
  destroyableEventListener as addEventListener,
  destroyableTimeout as startTimeout
} from "@nrk/destroyable";

// src/adapter/htmlmedia-subtitles.ts
import cuid from "cuid";
var logger = getLogger("HtmlMediaSubtitles");
var HtmlMediaSubtitles = class {
  element;
  eventEmitter;
  configs;
  nativeDisplayEnabled;
  activeConfig;
  activeTrack;
  constructor(element, eventEmitter, configs) {
    this.element = element;
    this.eventEmitter = eventEmitter;
    this.configs = configs ? configs.map((config) => Object.freeze(config)) : [];
    this.nativeDisplayEnabled = true;
  }
  supportsAddingTracks() {
    return true;
  }
  addSubtitlesTrack(src, label, language, variant, options) {
    if (!this.element) {
      throw new Error("Subtitle controls are destroyed.");
    }
    const id = `sub-${cuid.slug()}`;
    const { autoselect = false, defaultOn = false, forced = false } = options ?? {};
    const track = document.createElement("track");
    track.id = id;
    track.kind = "subtitles";
    track.label = label;
    track.src = src;
    track.srclang = language;
    track.onload = (event) => {
      const trackElement = event.target;
      trackElement.onload = null;
      const track2 = trackElement.track;
      const targetMode = this.activeTrack?.id === track2.id ? this.nativeDisplayEnabled ? "showing" : "hidden" : "disabled";
      if (track2.mode !== targetMode) {
        logger.log(`Subtitles ${id} (${label}/${language}) loaded as '${track2.mode}'. Expected '${targetMode}'.`);
        track2.mode = targetMode;
      } else {
        logger.log(`Subtitles ${id} (${label}/${language}) loaded as '${track2.mode}'.`);
      }
    };
    this.element.appendChild(track);
    const config = {
      id,
      src,
      label,
      language,
      variant,
      autoselect,
      defaultOn,
      forced
    };
    this.configs.push(Object.freeze(config));
    return Promise.resolve(config);
  }
  setNoSubtitles(options) {
    const forcedConfig = this.getForcedConfig();
    this.switchSubtitles(forcedConfig, options);
  }
  setDefaultSubtitles() {
    const options = { byUser: false };
    const defaultConfig = this.getDefaultConfig();
    if (defaultConfig) {
      this.switchSubtitles(defaultConfig, options);
      return;
    }
    this.setNoSubtitles(options);
  }
  setAutoSubtitles() {
    const options = { byUser: false };
    const defaultConfig = this.getDefaultConfig();
    if (defaultConfig) {
      this.switchSubtitles(defaultConfig, options);
      return;
    }
    const autoselectConfig = this.getAutoselectConfig();
    if (autoselectConfig) {
      this.switchSubtitles(autoselectConfig, options);
      return;
    }
    this.setNoSubtitles(options);
  }
  setActiveSubtitles(id, options) {
    const ident = typeof id === "object" ? id.id : id;
    const config = this.configs.find((config2) => config2.id === ident && !config2.forced);
    if (config) {
      this.switchSubtitles(config, options);
    }
  }
  getActiveSubtitles() {
    return this.activeConfig?.forced === false ? this.activeConfig : void 0;
  }
  getAvailableSubtitles() {
    return this.configs.filter((config) => !config.forced);
  }
  enableNativeSubtitlesDisplay() {
    if (!this.nativeDisplayEnabled) {
      this.nativeDisplayEnabled = true;
      if (this.activeTrack) {
        this.activeTrack.mode = "showing";
      }
      this.emitSubtitleDisplayChange();
    }
  }
  disableNativeSubtitlesDisplay() {
    if (this.nativeDisplayEnabled) {
      this.nativeDisplayEnabled = false;
      if (this.activeTrack) {
        this.activeTrack.mode = "hidden";
      }
      this.emitSubtitleDisplayChange();
    }
  }
  isNativeSubtitlesDisplayEnabled() {
    return this.nativeDisplayEnabled;
  }
  destroy() {
    if (this.activeTrack) {
      this.switchSubtitles(void 0, { byUser: false });
    }
    this.element = void 0;
  }
  getDefaultConfig() {
    return this.getBestLanguageMatch(this.configs.filter((config) => config.defaultOn && !config.forced));
  }
  getAutoselectConfig() {
    return this.getBestLanguageMatch(
      this.configs.filter((config) => (config.autoselect || config.defaultOn) && !config.forced)
    );
  }
  getForcedConfig() {
    return this.getBestLanguageMatch(this.configs.filter((config) => config.forced));
  }
  getBestLanguageMatch(configs) {
    if (configs.length === 0) {
      return void 0;
    }
    const preferredLanguages = getMatchLanguages(getClientLanguages());
    return orderByLanguage(configs, (config) => config.language, preferredLanguages)[0];
  }
  switchSubtitles(newConfig, options) {
    if (newConfig?.id === this.activeConfig?.id) {
      return;
    }
    if (this.activeTrack) {
      if (this.activeTrack.activeCues?.length ?? 0 > 0) {
        this.emitCueEvent();
      }
      this.activeTrack.mode = "disabled";
      this.activeTrack.oncuechange = null;
      this.activeTrack = void 0;
      this.activeConfig = void 0;
    }
    if (newConfig) {
      const newTrack = this.getTextTrack(newConfig);
      if (newTrack) {
        this.activeTrack = newTrack;
        this.activeConfig = newConfig;
        newTrack.oncuechange = () => this.emitCueEvent(this.activeTrack);
        newTrack.mode = this.nativeDisplayEnabled ? "showing" : "hidden";
      }
    }
    this.emitSubtitleChange(options?.byUser !== false);
  }
  /**
   * The key to a working subtitles implementation: The list of configurations
   * is the defined list of available subtitles, regardless of which text
   * tracks exist. There might be more text tracks than configurations
   * (hopefully not fewer). Subtitles sideloaded with `addSubtitlesTrack()`
   * have unique IDs, so their tracks are easy to find. If the list of
   * configurations is provided via the constructor, it's absolutely best if it
   * represents the full list of subtitle text tracks available on the media
   * element, in order to avoid bad lookups.
   */
  getTextTrack(config) {
    if (!this.element) {
      return void 0;
    }
    const textTrackList = this.element.textTracks;
    let track;
    if (textTrackList.getTrackById) {
      track = textTrackList.getTrackById(config.id);
    } else {
      track = this.element.querySelector(`#${config.id}`)?.track;
    }
    if (track) {
      return track;
    }
    for (let i = 0; i < textTrackList.length; ++i) {
      const t = textTrackList[i];
      if (t.kind === "subtitles" && t.label === config.label && t.language === config.language) {
        track = t;
        break;
      }
    }
    return track || void 0;
  }
  emitSubtitleChange(byUser) {
    this.eventEmitter.emit("playercore.subtitleschange" /* SubtitlesChange */, {
      byUser,
      active: this.activeConfig?.forced === false ? this.activeConfig : void 0
    });
  }
  emitSubtitleDisplayChange() {
    this.eventEmitter.emit("playercore.subtitlesdisplaychange" /* SubtitlesDisplayChange */, {
      nativeEnabled: this.nativeDisplayEnabled
    });
  }
  emitCueEvent(track) {
    this.eventEmitter.emit("playercore.subtitlescueupdate" /* SubtitlesCueUpdate */, {
      activeCues: Array.from(track?.activeCues ?? [])
    });
  }
};

// src/adapter/htmlmedia-time-range-events.ts
import { EventEmitter } from "eventemitter3";
import { DestroyableBase } from "@nrk/destroyable";
var ALL_RANGES = "*";
var HtmlMediaTimeRangeEvents = class extends DestroyableBase {
  textTrack;
  data = {};
  eventEmitter = new EventEmitter();
  idCounter = 0;
  onCueEnter;
  onCueExit;
  constructor(mediaElement) {
    super();
    this.textTrack = mediaElement.addTextTrack("metadata");
    this.onCueEnter = this.cueEventHandler.bind(this, "enter");
    this.onCueExit = this.cueEventHandler.bind(this, "exit");
  }
  addRange(range, ...dataArg) {
    const data = dataArg.length ? dataArg[0] : void 0;
    const rangeId = this.generateId();
    this.data[rangeId] = data;
    const cue = this.createTextTackCue(rangeId, range.start, range.end);
    cue.addEventListener("enter", this.onCueEnter);
    cue.addEventListener("exit", this.onCueExit);
    this.textTrack.addCue(cue);
    return this.createExtendedTimeRange(rangeId, range.start, range.end);
  }
  getAllRanges() {
    return Array.from(this.textTrack.cues ?? []).map((cue) => {
      return this.createExtendedTimeRange(cue.id, cue.startTime, cue.endTime);
    });
  }
  getActiveRanges() {
    return Array.from(this.textTrack.activeCues ?? []).map((cue) => {
      return this.createExtendedTimeRange(cue.id, cue.startTime, cue.endTime);
    });
  }
  removeRange(range) {
    const rangeId = typeof range === "string" ? range : range.id;
    const cue = this.textTrack.cues?.getCueById(rangeId);
    if (cue) {
      delete this.data[rangeId];
      cue.removeEventListener("enter", this.onCueEnter);
      cue.removeEventListener("exit", this.onCueExit);
      this.textTrack.removeCue(cue);
    }
    this.removeAllListeners(rangeId);
  }
  removeAllRanges() {
    while (this.textTrack.cues?.length) {
      const cue = this.textTrack.cues[0];
      this.textTrack.removeCue(cue);
      delete this.data[cue.id];
    }
  }
  on(type, handler, context) {
    this.eventEmitter.on(this.getEventId(ALL_RANGES, type), handler, context);
  }
  once(type, handler, context) {
    this.eventEmitter.once(this.getEventId(ALL_RANGES, type), handler, context);
  }
  off(type, handler, context) {
    this.eventEmitter.off(this.getEventId(ALL_RANGES, type), handler, context);
  }
  removeAllListeners(rangeId) {
    this.eventEmitter.removeAllListeners(this.getEventId(rangeId ?? ALL_RANGES, "enter"));
    this.eventEmitter.removeAllListeners(this.getEventId(rangeId ?? ALL_RANGES, "exit"));
  }
  destroy() {
    this.eventEmitter.removeAllListeners();
    this.removeAllRanges();
    this.textTrack.mode = "disabled";
    super.destroy();
  }
  createExtendedTimeRange(id, start, end) {
    return {
      id,
      start,
      end,
      data: this.data[id],
      on: (type, handler, context) => {
        this.eventEmitter.on(this.getEventId(id, type), handler, context);
      },
      once: (type, handler, context) => {
        this.eventEmitter.once(this.getEventId(id, type), handler, context);
      },
      off: (type, handler, context) => {
        this.eventEmitter.off(this.getEventId(id, type), handler, context);
      },
      removeAllListeners: () => {
        this.removeAllListeners(id);
      }
    };
  }
  createTextTackCue(id, start, end) {
    const Cue = "VTTCue" in window ? VTTCue : TextTrackCue;
    const cue = new Cue(start, end, id);
    cue.id = id;
    return cue;
  }
  cueEventHandler(type, event) {
    const cue = event.target;
    const timeRangeEvent = Object.freeze({
      type,
      range: this.createExtendedTimeRange(cue.id, cue.startTime, cue.endTime)
    });
    this.eventEmitter.emit(this.getEventId(cue.id, type), timeRangeEvent);
    this.eventEmitter.emit(this.getEventId(ALL_RANGES, type), timeRangeEvent);
  }
  generateId() {
    return `tr:${this.idCounter++}`;
  }
  getEventId(rangeId, eventType) {
    return `${rangeId}:${eventType}`;
  }
};

// src/adapter/adapter-base-htmlmedia.ts
import cuid2 from "cuid";
var logger2 = getLogger("AdapterBaseHtmlMedia");
var TIME_NOT_STARTING_TIMEOUT = 2e3;
var TIME_NOT_RUNNING_TIMEOUT = 1e3;
var AdapterBaseHtmlMedia = class extends AdapterBaseTime {
  mediaElement;
  mediaElementSessionId = cuid2.slug();
  preloading;
  preloadDestroyables;
  preloaded = false;
  incomingPauseTime;
  incomingUnmuteTime;
  positionAtPlayingEvent;
  positionJustBeforeSeeking;
  positionSeekTarget;
  constructor(context) {
    super(context.eventEmitter, context.liveOptions);
    this.mediaElement = context.mediaElement;
    this.registerHtmlMediaEventListeners();
  }
  async preload(options) {
    if (this.preloading === void 0) {
      logger2.log("Preloading.");
      this.mediaElement.dataset.sessionId = this.mediaElementSessionId;
      this.registerHtmlMediaPlaybackListeners();
      this.preloadDestroyables = new DestroyableSet(this);
      this.preloading = this.internalPreload(this.preloadDestroyables, options).then(() => {
        logger2.log("Preload done.");
        this.eventEmitter.emit("playercore.mediainfoloaded" /* MediaInfoLoaded */, this.getMediaInfo());
        if (this.mediaRange !== void 0 && this.mediaRange.end > 0 && Number.isFinite(this.mediaRange.end)) {
          this.eventEmitter.emit("playercore.mediarangechange" /* MediaRangeChange */, this.mediaRange);
        }
        this.preloaded = true;
        if (this.preloadDestroyables !== void 0) {
          this.preloadDestroyables.destroy();
          this.preloadDestroyables = void 0;
        }
      }).catch((e) => {
        if (isAbortError(e)) {
          logger2.warn("Preload aborted:", e);
        } else {
          logger2.error("Preload failed:", e);
          const error = PlaybackSessionError.fromUnknownError(e, this.playbackStarted, "Failed to preload");
          this.handleError(error);
        }
        if (this.preloadDestroyables !== void 0) {
          this.preloadDestroyables.destroy();
          this.preloadDestroyables = void 0;
        }
      });
    } else {
      logger2.log("Preload already initiated.");
    }
    return this.preloading;
  }
  async play() {
    if (this.stateMachine.is("idle" /* Idle */)) {
      this.stateMachine.go("starting" /* Starting */);
      if (!this.preloaded) {
        logger2.log("Waiting for preload to complete.");
        await this.preload({ loadData: true });
      }
      return this.internalPlay("starting" /* Starting */);
    } else if (this.stateMachine.is("paused" /* Paused */) || this.stateMachine.is("paused-seeking" /* PausedSeeking */) || this.stateMachine.is("autoplayblocked" /* AutoplayBlocked */) || this.stateMachine.is("ended" /* Ended */)) {
      const expectEvent = new DestroyableSet(this);
      expectEvent.addDestroyables([
        addEventListener(this.mediaElement, "playing", () => expectEvent.destroy()),
        addEventListener(this.mediaElement, "pause", () => expectEvent.destroy()),
        startTimeout(() => {
          expectEvent.destroy();
          logger2.log("Still waiting for playback (play).");
          this.stateMachine.go("rebuffering" /* Rebuffering */);
        }, TIME_NOT_STARTING_TIMEOUT)
      ]);
      return this.internalPlay();
    }
  }
  pause() {
    if (!this.mediaElement.paused) {
      this.incomingPauseTime = Date.now();
      this.mediaElement.pause();
    } else if (this.stateMachine.is("starting" /* Starting */)) {
      this.stateMachine.go("paused" /* Paused */);
      logger2.log("Pause: Interrupting starting by changing to paused state.");
    }
  }
  seekTo(position) {
    this.positionSeekTarget = toWithinRange(this.getSeekableRange(), position);
    if (this.positionJustBeforeSeeking === void 0) {
      this.positionJustBeforeSeeking = this.mediaElement.currentTime;
    }
    this.mediaElement.currentTime = this.positionSeekTarget;
  }
  getCurrentTime() {
    if (this.positionSeekTarget !== void 0) {
      return this.positionSeekTarget;
    }
    return Math.max(this.positionAtPlayingEvent ?? 0, this.mediaElement.currentTime);
  }
  /**
   * Call play after a potential state check. Override to apply more
   * functionality.
   */
  internalPlay(requiredState) {
    if (requiredState !== void 0 && !this.stateMachine.is(requiredState)) {
      logger2.log(`Not calling play. State is '${this.stateMachine.currentState}'. Wanted '${requiredState}'.`);
      return Promise.resolve();
    }
    if (this.stateMachine.is("broken" /* Broken */) || this.destroyed) {
      logger2.log("Not calling play. Adapter is broken or destroyed.");
      return Promise.resolve();
    }
    logger2.log("Calling play().");
    return this.mediaElement.play().catch((err) => {
      const now = Date.now();
      if (this.incomingPauseTime !== void 0 && now - this.incomingPauseTime < 1e3) {
        logger2.log("Play interrupted by call to pause:", err);
        return;
      }
      if (this.stateMachine.is("broken" /* Broken */)) {
        logger2.log("Play failed due to broken media:", err);
        return;
      }
      this.stateMachine.go("autoplayblocked" /* AutoplayBlocked */);
      throw new AutoplayError("Playback denied due to autoplay policy", toError(err));
    });
  }
  /**
   * Emit `MediaRangeChange` events, but only after preloading is done and
   * `MediaInfoLoaded` is emitted. See `preload()`.
   */
  handleMediaRangeUpdate(mediaRange, livePosition, programDateTime) {
    const previousMediaRange = this.mediaRange;
    super.handleMediaRangeUpdate(mediaRange, livePosition, programDateTime);
    if (this.preloaded && Number.isFinite(mediaRange.end) && mediaRange.end > 0 && (previousMediaRange?.start !== mediaRange.start || previousMediaRange?.end !== mediaRange.end)) {
      this.eventEmitter.emit("playercore.mediarangechange" /* MediaRangeChange */, mediaRange);
    }
  }
  // Subtitles.
  async addSubtitlesTrack(src, label, language, variant, options) {
    if (this.subtitleControls?.supportsAddingTracks() !== true) {
      let nativeDisplayEnabled;
      if (this.subtitleControls !== void 0) {
        logger2.log("Changing from media provided subtitles to sideloaded subtitles.");
        nativeDisplayEnabled = this.subtitleControls.isNativeSubtitlesDisplayEnabled();
        this.subtitleControls.destroy?.();
      } else {
        logger2.log("Creating controls for sideloaded subtitles.");
      }
      this.subtitleControls = new HtmlMediaSubtitles(this.mediaElement, this.eventEmitter);
      if (nativeDisplayEnabled !== void 0) {
        if (nativeDisplayEnabled) {
          this.subtitleControls.enableNativeSubtitlesDisplay();
        } else {
          this.subtitleControls.disableNativeSubtitlesDisplay();
        }
      }
    }
    return this.subtitleControls.addSubtitlesTrack(src, label, language, variant, options);
  }
  // Playback rate.
  setPlaybackRate(rate) {
    this.mediaElement.playbackRate = rate;
  }
  getPlaybackRate() {
    return this.mediaElement.playbackRate;
  }
  resetPlaybackRate() {
    this.mediaElement.playbackRate = this.mediaElement.defaultPlaybackRate;
  }
  // Mute/volume.
  mute() {
    this.mediaElement.muted = true;
  }
  unmute() {
    this.mediaElement.muted = false;
  }
  isMuted() {
    return this.mediaElement.muted;
  }
  setVolume(volume) {
    this.mediaElement.volume = volume;
  }
  getVolume() {
    return this.mediaElement.volume;
  }
  // Time range events.
  createTimeRangeEventTrack() {
    const track = new HtmlMediaTimeRangeEvents(this.mediaElement);
    this.addDestroyable(track);
    return track;
  }
  // Media element events ++.
  /**
   * Override to mark seeking events as automatic, which should not cause the
   * adapter to change state.
   */
  isAutoSeek() {
    return false;
  }
  /**
   * Check if current time is at the end (or beyond the end) of the stream. It
   * should always return false for live and ongoing event streams. Makes it
   * possble to avoid going via paused when a stream ends with both paused and
   * ended events
   */
  endOfMediaReached() {
    if (this.isLiveStream() && !this.isLiveStreamEnded()) {
      return false;
    }
    return this.getCurrentTime() >= this.getMediaRange().end;
  }
  /**
   * Used by super. Returns the position of the player when the time "just"
   * stopped running, for the TimeRunningChange event. Current time can be
   * completely wrong, for example if time stopped in relation to seeking and
   * current time is already pointing to the seek target.
   */
  getTimeStoppedPosition() {
    return this.positionJustBeforeSeeking ?? this.getCurrentTime();
  }
  /**
   * Used by super. Returns the position of the player when the time "just"
   * started running, for the TimeRunningChange event. Smooths over rough
   * seeking and "slow" detection of playing.
   */
  getTimeStartedPosition() {
    return this.positionSeekTarget ?? this.positionAtPlayingEvent ?? this.getCurrentTime();
  }
  /**
   * Listen to media events and keep the media element state and adapter state
   * in sync. Smoothing over client differences. It's probably best to set up
   * other event listener first so that the event listeners below can operate
   * on correct information.
   */
  registerHtmlMediaPlaybackListeners() {
    let isSeeking = false;
    let lastTimeUpdate;
    let wasAutoplayBlockedBeforeSeeking = false;
    let missingTimeUpdateTimeout;
    let stateHooks = new DestroyableSet(this);
    const handlers = {
      playing: () => {
        this.positionAtPlayingEvent = this.positionSeekTarget ?? this.mediaElement.currentTime;
      },
      seeking: () => {
        isSeeking = true;
        logger2.log(`Is seeking from ${lastTimeUpdate} to ${this.getCurrentTime()}`);
        if (!this.isAutoSeek()) {
          if (this.positionJustBeforeSeeking === void 0) {
            this.positionJustBeforeSeeking = lastTimeUpdate;
          }
          this.positionAtPlayingEvent = void 0;
        }
      },
      seeked: () => {
        isSeeking = false;
        lastTimeUpdate = void 0;
      },
      ended: () => {
        this.stateMachine.go("ended" /* Ended */);
        this.positionAtPlayingEvent = void 0;
      },
      timeupdate: () => {
        const actualTime = this.mediaElement.currentTime;
        let eventTime = actualTime;
        if (!isSeeking && this.positionSeekTarget !== void 0) {
          if (actualTime >= this.positionSeekTarget || actualTime < this.positionSeekTarget - 3) {
            this.positionSeekTarget = void 0;
            logger2.log("Seek target cleared.");
          } else {
            eventTime = this.positionSeekTarget;
          }
        } else if (this.positionAtPlayingEvent !== void 0) {
          eventTime = Math.max(this.positionAtPlayingEvent, actualTime);
        }
        if (actualTime === lastTimeUpdate) {
          return;
        }
        lastTimeUpdate = actualTime;
        stateHandlers[this.stateMachine.currentState].timeupdate?.();
        this.eventEmitter.emit("playercore.timeupdate" /* TimeUpdate */, eventTime);
      }
    };
    const stateHandlers = {
      ["idle" /* Idle */]: {
        play: () => {
          this.play().catch(logger2.warn.bind(logger2));
        }
      },
      ["starting" /* Starting */]: {
        _exit: () => {
          this.positionJustBeforeSeeking = void 0;
        },
        playing: () => {
          stateHooks.addDestroyable(
            addEventListener(this.mediaElement, "timeupdate", () => {
              const currentTime = this.mediaElement.currentTime;
              if (!isSeeking && currentTime > 0) {
                logger2.log("Got first non-zero time update after playing event:", currentTime);
                this.stateMachine.go("playing" /* Playing */);
              }
            })
          );
        },
        pause: () => {
          const now = Date.now();
          const incomingPauseDelay = this.incomingPauseTime !== void 0 ? now - this.incomingPauseTime : void 0;
          const incomingUnmuteDelay = this.incomingUnmuteTime !== void 0 ? now - this.incomingUnmuteTime : void 0;
          if (incomingPauseDelay !== void 0 && incomingPauseDelay < 500) {
            logger2.metric("pause-call-to-event-delay-starting", incomingPauseDelay);
            this.stateMachine.go("paused" /* Paused */);
            return;
          }
          if (incomingUnmuteDelay !== void 0 && incomingUnmuteDelay < 100) {
            logger2.metric("unmute-event-to-pause-event-starting", incomingUnmuteDelay);
            this.stateMachine.go("autoplayblocked" /* AutoplayBlocked */);
          }
        }
      },
      ["playing" /* Playing */]: {
        _enter: () => {
          stateHooks.addEventListener("destroy", () => {
            if (missingTimeUpdateTimeout !== void 0) {
              clearTimeout(missingTimeUpdateTimeout);
              missingTimeUpdateTimeout = void 0;
            }
          });
          missingTimeUpdateTimeout = setTimeout(() => {
            logger2.log("Missing time update after playing. Changing to rebuffering.");
            this.stateMachine.go("rebuffering" /* Rebuffering */);
          }, TIME_NOT_STARTING_TIMEOUT);
        },
        timeupdate: () => {
          if (missingTimeUpdateTimeout !== void 0) {
            clearTimeout(missingTimeUpdateTimeout);
          }
          missingTimeUpdateTimeout = setTimeout(() => {
            logger2.log("Missing time update. Changing to rebuffering.");
            this.stateMachine.go("rebuffering" /* Rebuffering */);
          }, TIME_NOT_RUNNING_TIMEOUT);
        },
        pause: () => {
          const now = Date.now();
          const incomingPauseDelay = this.incomingPauseTime !== void 0 ? now - this.incomingPauseTime : void 0;
          const incomingUnmuteDelay = this.incomingUnmuteTime !== void 0 ? now - this.incomingUnmuteTime : void 0;
          if (this.endOfMediaReached()) {
            this.stateMachine.go("ended" /* Ended */);
            return;
          }
          if (incomingPauseDelay !== void 0 && incomingPauseDelay < 500) {
            logger2.metric("pause-call-to-event-delay-playing", incomingPauseDelay);
            this.stateMachine.go("paused" /* Paused */);
            return;
          }
          if (incomingUnmuteDelay !== void 0 && incomingUnmuteDelay < 100) {
            logger2.metric("unmute-event-to-pause-event-playing", incomingUnmuteDelay);
            this.stateMachine.go("autoplayblocked" /* AutoplayBlocked */);
          }
          const hooks = new DestroyableSet(stateHooks);
          hooks.addDestroyables([
            addEventListener(this.mediaElement, "seeking", () => hooks.destroy()),
            addEventListener(this.mediaElement, "playing", () => hooks.destroy()),
            addEventListener(this.mediaElement, "play", () => this.stateMachine.go("rebuffering" /* Rebuffering */)),
            startTimeout(() => {
              hooks.destroy();
              logger2.log("Unexpected pause event. Not caused by call to pause, autoplay policy or seeking.");
              this.stateMachine.go("paused" /* Paused */);
            }, 100)
          ]);
        },
        seeking: () => {
          if (!this.isAutoSeek()) {
            this.stateMachine.go("seeking" /* Seeking */);
          }
        }
      },
      ["paused" /* Paused */]: {
        play: () => {
          const noPlayback = new DestroyableSet(stateHooks);
          noPlayback.addDestroyables([
            addEventListener(this.mediaElement, "pause", () => noPlayback.destroy()),
            startTimeout(() => {
              logger2.log("Still waiting for playback (paused).");
              this.stateMachine.go("rebuffering" /* Rebuffering */);
            }, TIME_NOT_STARTING_TIMEOUT)
          ]);
        },
        playing: () => {
          this.stateMachine.go("playing" /* Playing */);
        },
        seeking: () => {
          wasAutoplayBlockedBeforeSeeking = false;
          this.stateMachine.go("paused-seeking" /* PausedSeeking */);
        }
      },
      ["autoplayblocked" /* AutoplayBlocked */]: {
        play: () => {
          const noPlayback = new DestroyableSet(stateHooks);
          noPlayback.addDestroyables([
            addEventListener(this.mediaElement, "pause", () => noPlayback.destroy()),
            startTimeout(() => {
              logger2.log("Still waiting for playback (autoplay-blocked).");
              this.stateMachine.go("rebuffering" /* Rebuffering */);
            }, TIME_NOT_STARTING_TIMEOUT)
          ]);
        },
        playing: () => {
          this.stateMachine.go("playing" /* Playing */);
        },
        seeking: () => {
          wasAutoplayBlockedBeforeSeeking = true;
          this.stateMachine.go("paused-seeking" /* PausedSeeking */);
        }
      },
      ["seeking" /* Seeking */]: {
        _exit: () => {
          this.positionJustBeforeSeeking = void 0;
        },
        pause: () => {
          if (this.endOfMediaReached()) {
            this.stateMachine.go("ended" /* Ended */);
          } else {
            this.stateMachine.go("paused" /* Paused */);
          }
        },
        playing: () => {
          if (!isSeeking) {
            this.stateMachine.go("playing" /* Playing */);
          }
        },
        timeupdate: () => {
          if (!isSeeking) {
            this.stateMachine.go("playing" /* Playing */);
          }
        }
      },
      ["paused-seeking" /* PausedSeeking */]: {
        _exit: () => {
          this.positionJustBeforeSeeking = void 0;
        },
        play: () => {
          this.stateMachine.go("seeking" /* Seeking */);
        },
        playing: () => {
          this.stateMachine.go("seeking" /* Seeking */);
        },
        seeked: () => {
          if (wasAutoplayBlockedBeforeSeeking) {
            this.stateMachine.go("autoplayblocked" /* AutoplayBlocked */);
          } else {
            this.stateMachine.go("paused" /* Paused */);
          }
        }
      },
      ["rebuffering" /* Rebuffering */]: {
        timeupdate: () => {
          this.stateMachine.go("playing" /* Playing */);
        },
        seeking: () => {
          if (!this.isAutoSeek()) {
            this.stateMachine.go("seeking" /* Seeking */);
          }
        },
        pause: () => {
          if (this.endOfMediaReached()) {
            this.stateMachine.go("ended" /* Ended */);
          } else {
            this.stateMachine.go("paused" /* Paused */);
          }
        }
      },
      ["ended" /* Ended */]: {
        seeking: () => {
          wasAutoplayBlockedBeforeSeeking = false;
          this.stateMachine.go("paused-seeking" /* PausedSeeking */);
        }
      },
      ["broken" /* Broken */]: {
        // End of the world.
      }
    };
    const previousTransitionHandler = this.stateMachine.onTransition;
    this.stateMachine.onTransition = (from, to) => {
      stateHandlers[from]._exit?.();
      stateHooks.destroy();
      stateHooks = new DestroyableSet(this);
      stateHandlers[to]._enter?.();
      previousTransitionHandler(from, to);
    };
    const handleEvent = (type) => {
      if (type === "timeupdate") {
        handlers.timeupdate();
      } else {
        logger2.debug(`Got ${type} event.`);
        handlers[type]?.();
        stateHandlers[this.stateMachine.currentState][type]?.();
      }
    };
    const mediaEventTypes = /* @__PURE__ */ new Set();
    Object.keys(handlers).forEach((eventType) => mediaEventTypes.add(eventType));
    Object.values(stateHandlers).forEach((handlers2) => {
      Object.keys(handlers2).forEach((eventType) => mediaEventTypes.add(eventType));
    });
    logger2.debug(`Used media event types: ${Array.from(mediaEventTypes)}`);
    this.addDestroyables(
      Array.from(mediaEventTypes).map(
        (eventType) => addEventListener(this.mediaElement, eventType, () => handleEvent(eventType))
      )
    );
  }
  /**
   * Handle playback rate, volume/muted and error events.
   */
  registerHtmlMediaEventListeners() {
    let previouslyMuted = this.isMuted();
    let previousVolume = this.getVolume();
    this.addDestroyables([
      addEventListener(this.mediaElement, "ratechange", () => {
        this.eventEmitter.emit("playercore.ratechange" /* RateChange */, this.getPlaybackRate());
      }),
      addEventListener(this.mediaElement, "volumechange", () => {
        const muted = this.isMuted();
        const volume = this.getVolume();
        if (previouslyMuted !== muted) {
          this.incomingUnmuteTime = muted ? void 0 : Date.now();
          previouslyMuted = muted;
          this.eventEmitter.emit("playercore.mutedchange" /* MutedChange */, muted);
        }
        if (previousVolume !== volume) {
          previousVolume = volume;
          this.eventEmitter.emit("playercore.volumechange" /* VolumeChange */, volume);
        }
      }),
      addEventListener(this.mediaElement, "error", () => {
        if (this.mediaElement.error === null) {
          logger2.error("Got HTMLMediaElement error event but no MediaError.");
          return;
        }
        const error = PlaybackSessionError.fromMediaError(this.mediaElement.error, this.playbackStarted);
        this.handleError(error);
      })
    ]);
  }
  async destroy() {
    if (this.mediaElement.dataset.sessionId === this.mediaElementSessionId) {
      if (!this.mediaElement.paused) {
        this.mediaElement.pause();
      }
      this.mediaElement.src = "";
    } else {
      logger2.log("Media element not last used by us. Not touching.");
    }
    super.destroy();
    return Promise.resolve();
  }
  // Extra functionality. Not part of any interface.
  fakeMediaErrorNetwork() {
    this.fakeMediaError(MediaError.MEDIA_ERR_NETWORK);
  }
  fakeMediaErrorDecode() {
    this.fakeMediaError(MediaError.MEDIA_ERR_DECODE);
  }
  fakeMediaError(code) {
    const codeMap = {
      MEDIA_ERR_ABORTED: MediaError.MEDIA_ERR_ABORTED,
      MEDIA_ERR_DECODE: MediaError.MEDIA_ERR_DECODE,
      MEDIA_ERR_NETWORK: MediaError.MEDIA_ERR_NETWORK,
      MEDIA_ERR_SRC_NOT_SUPPORTED: MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
    };
    const errorName = Object.keys(codeMap).find((key) => codeMap[key] === code);
    if (errorName === void 0) {
      console.log(`Supported error codes: ${Object.values(codeMap).join(", ")}`);
      return;
    }
    const error = {
      code,
      message: `Fake ${errorName}`,
      ...codeMap
    };
    this.handleError(PlaybackSessionError.fromMediaError(error, this.playbackStarted));
    if (!this.stateMachine.is("broken" /* Broken */)) {
      this.stateMachine.go("rebuffering" /* Rebuffering */);
    }
  }
};

export {
  HtmlMediaSubtitles,
  AdapterBaseHtmlMedia
};
